/**
 * Structured Data Component
 * Adds Schema.org JSON-LD for SEO and AI discoverability
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function StructuredData() {
  const location = useLocation();

  useEffect(() => {
    // Remove any existing structured data
    const existing = document.querySelectorAll('script[type="application/ld+json"]');
    existing.forEach(script => script.remove());

    // Base URL (update this to your actual domain)
    const baseUrl = "https://sbu-ug8.pages.dev";

    // LocalBusiness Schema - Always present
    const localBusiness = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${baseUrl}/#organization`,
      "name": "Marketing For Schools",
      "alternateName": "MFS",
      "description": "Integrated marketing services for schools across Kent and SE England - photography, videography, design, signage, and social media support. One team, everything you need.",
      "url": baseUrl,
      "telephone": "+44-845-838-0557",
      "email": "hello@marketingforschools.co.uk",
      "founder": {
        "@type": "Person",
        "name": "Stephen O'Halloran",
        "jobTitle": "Founder & Lead Videographer"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "75 Westbrook Avenue",
        "addressLocality": "Margate",
        "addressRegion": "Kent",
        "postalCode": "CT9 5HB",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "51.3813",
        "longitude": "1.3862"
      },
      "areaServed": [
        {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "latitude": "51.3813",
            "longitude": "1.3862"
          },
          "geoRadius": "50000"
        },
        { "@type": "Place", "name": "Kent, England" },
        { "@type": "Place", "name": "South East England" },
        { "@type": "Place", "name": "Canterbury" },
        { "@type": "Place", "name": "Thanet" },
        { "@type": "Place", "name": "Margate" },
        { "@type": "Place", "name": "Broadstairs" },
        { "@type": "Place", "name": "Ramsgate" },
        { "@type": "Place", "name": "Whitstable" },
        { "@type": "Place", "name": "Herne Bay" },
        { "@type": "Place", "name": "Faversham" },
        { "@type": "Place", "name": "Dover" },
        { "@type": "Place", "name": "Deal" },
        { "@type": "Place", "name": "Sandwich" },
        { "@type": "Place", "name": "Maidstone" },
        { "@type": "Place", "name": "Tunbridge Wells" },
        { "@type": "Place", "name": "Tonbridge" },
        { "@type": "Place", "name": "Sevenoaks" },
        { "@type": "Place", "name": "Gravesend" },
        { "@type": "Place", "name": "Dartford" },
        { "@type": "Place", "name": "Swanley" },
        { "@type": "Place", "name": "Sittingbourne" },
        { "@type": "Place", "name": "Sheerness" },
        { "@type": "Place", "name": "Ashford" },
        { "@type": "Place", "name": "Folkestone" }
      ],
      "priceRange": "££",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      "sameAs": [
        "https://twitter.com/ShotbyU",
        "https://www.youtube.com/channel/UCfJy50YSU4qXCyaP4zZZLHA",
        "https://vimeo.com/shotbyu"
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Video Production Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "School Video Production",
              "description": "Cinematic short films showcasing school spirit, values, and achievements"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "School Photography",
              "description": "Natural, candid moments captured beautifully"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Wall Art & Signage",
              "description": "Custom prints and designs for school spaces"
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "5.0",
        "reviewCount": "4",
        "bestRating": "5",
        "worstRating": "1"
      }
    };

    // ProfessionalService Schema
    const professionalService = {
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Marketing For Schools - Integrated Marketing Services",
      "image": `${baseUrl}/og-image.jpg`,
      "description": "Integrated marketing services for schools across Kent - photography, videography, design, signage, and social media. 15+ years experience.",
      "priceRange": "££",
      "serviceType": "Marketing Services",
      "provider": {
        "@id": `${baseUrl}/#organization`
      },
      "areaServed": {
        "@type": "State",
        "name": "Kent"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Professional Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "serviceType": "Video Production",
              "name": "School Promotional Videos",
              "description": "Professional promotional videos for schools"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "serviceType": "Photography",
              "name": "Educational Photography",
              "description": "Professional photography for educational institutions"
            }
          }
        ]
      }
    };

    // WebSite Schema for enhanced search
    const website = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Marketing For Schools",
      "url": baseUrl,
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": `${baseUrl}/?s={search_term_string}`
        },
        "query-input": "required name=search_term_string"
      }
    };

    // BreadcrumbList for navigation
    const breadcrumbs: any = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        }
      ]
    };

    if (location.pathname === "/schools") {
      breadcrumbs.itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Schools Portfolio",
        "item": `${baseUrl}/schools`
      });
    } else if (location.pathname === "/contact") {
      breadcrumbs.itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Contact",
        "item": `${baseUrl}/contact`
      });
    }

    // Combine all schemas
    const schemas = [localBusiness, professionalService, website, breadcrumbs];

    // Add FAQPage and Review schemas for home page
    if (location.pathname === "/") {
      const faqPage = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What areas do you cover for school video production?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We cover all of Kent including Canterbury, Thanet, Margate, and surrounding areas. We've worked with over 50 schools across the region."
            }
          },
          {
            "@type": "Question",
            "name": "How long does it take to produce a school video?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Typically 2-3 weeks from filming to final delivery. We work with minimal disruption to your school day, filming authentic moments and professional editing with revisions included."
            }
          },
          {
            "@type": "Question",
            "name": "What makes Marketing For Schools different from other marketing agencies?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "We specialise exclusively in school marketing with over 15 years of experience across Kent. Our integrated approach means one team handles photography, video, design, signage, and social media - no more coordinating multiple suppliers. We understand safeguarding, term schedules, and how to work around busy school days."
            }
          }
        ]
      };
      schemas.push(faqPage);

      // Mixed client reviews (homepage - schools + business)
      const homeReviews = [
        {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "@id": `${baseUrl}/#organization`
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "name": "Amazing service and care",
          "reviewBody": "The company that keeps giving we are thrilled with our videos and amazed by the service care and consideration we recieved.",
          "author": {
            "@type": "EducationalOrganization",
            "name": "Ellington Infants School"
          },
          "publisher": {
            "@type": "EducationalOrganization",
            "name": "Ellington Infants School"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "@id": `${baseUrl}/#organization`
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "name": "Quick creative and on message",
          "reviewBody": "Steve and his team deliver to the letter and more.",
          "author": {
            "@type": "Organization",
            "name": "Spurling Cannon"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Spurling Cannon"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "@id": `${baseUrl}/#organization`
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "name": "Formidable operator",
          "reviewBody": "Thanks for the really good job you have done for Claimaday.com. We will not only want to use your services again, but will pass the good news of your existence as a formidable operator in your field to those who ask, already a couple!",
          "author": {
            "@type": "Organization",
            "name": "Claimaday.com"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Claimaday.com"
          }
        }
      ];
      schemas.push(...homeReviews);
    }

    // School reviews (schools page)
    if (location.pathname === "/schools") {
      const schoolReviews = [
        {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "@id": `${baseUrl}/#organization`
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "name": "Amazing service and care",
          "reviewBody": "The company that keeps giving we are thrilled with our videos and amazed by the service care and consideration we recieved.",
          "author": {
            "@type": "EducationalOrganization",
            "name": "Ellington Infants School"
          },
          "publisher": {
            "@type": "EducationalOrganization",
            "name": "Ellington Infants School"
          }
        },
        {
          "@context": "https://schema.org",
          "@type": "Review",
          "itemReviewed": {
            "@type": "LocalBusiness",
            "@id": `${baseUrl}/#organization`
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5",
            "bestRating": "5"
          },
          "name": "Warm, joyful, and honest",
          "reviewBody": "Stephen captured our school perfectly — warm, joyful, honest. Parents said it gave them goosebumps.",
          "author": {
            "@type": "EducationalOrganization",
            "name": "Kent Primary School"
          },
          "publisher": {
            "@type": "EducationalOrganization",
            "name": "Kent Primary School"
          }
        }
      ];
      schemas.push(...schoolReviews);
    }

    // Inject all schemas
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    return () => {
      // Cleanup on unmount
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, [location.pathname]);

  return null; // This component doesn't render anything
}
